import React, { useEffect, useMemo, useState } from 'react';
import '../boatBooking.css';
import { collection, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { addDaysToDateISO, todayUKDateISO, ukDateISOToUTCEnd, ukDateISOToUTCStart } from '../../../../utils/ukDates';
import { safeSailingBoatLabel } from '../../utils/sailingFleet';
import { isSailingBoatBookingEnabled } from '../../utils/moduleUtils';
import EditButton from '../../../ui/EditButton';
import { CalendarButton } from '../../../ui/CalendarButton';
import BoatCalendarModal from '../../components/modals/rowing/BoatCalendarModal';
import { formatSailingBoatMoney } from '../../utils/sailingBoatPayments';

function formatDate(value) {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!date) return 'Date TBC';
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function statusClass(status) {
  const normalized = String(status || 'pending');
  if (normalized === 'allocated' || normalized === 'confirmed') return 'status-pill success';
  if (normalized === 'cancelled' || normalized === 'unfilled') return 'status-pill danger';
  return 'status-pill info';
}

export default function SailingBoatBookingCard({
  isAdmin,
  teamId,
  modules = {},
  rules = {},
  openCalendar = false,
  onEditRules,
  onRequestBoat,
}) {
  const enabled = isSailingBoatBookingEnabled(modules);
  const [boats, setBoats] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState('');
  const [error, setError] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [charges, setCharges] = useState([]);
  const [payingChargeId, setPayingChargeId] = useState('');
  useEffect(() => {
    if (openCalendar) setCalendarOpen(true);
  }, [openCalendar]);
  const [startISO, setStartISO] = useState(todayUKDateISO());
  const [endISO, setEndISO] = useState(addDaysToDateISO(todayUKDateISO(), 14));

  useEffect(() => {
    if (!enabled || !teamId) return undefined;
    const unsubscribe = safeOnSnapshot(collection(db, 'teams', teamId, 'sailingBoats'), (snapshot) => {
      setBoats(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }, undefined, { label: 'sailing-boat-booking-fleet', teamId });
    return () => unsubscribe();
  }, [enabled, teamId]);

  useEffect(() => {
    if (!enabled || !teamId) return undefined;
    const uid = getAuth().currentUser?.uid;
    if (!uid) {
      setRequests([]);
      return undefined;
    }
    setLoading(true);
    const requestQuery = query(
      collection(db, 'teams', teamId, 'sailingBoatRequests'),
      where('memberId', '==', uid),
      where('date', '>=', Timestamp.fromDate(ukDateISOToUTCStart(startISO))),
      where('date', '<=', Timestamp.fromDate(ukDateISOToUTCEnd(endISO))),
      orderBy('date', 'asc')
    );
    const unsubscribe = safeOnSnapshot(requestQuery, (snapshot) => {
      setRequests(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setLoading(false);
    }, () => setLoading(false), { label: 'sailing-boat-booking-member-requests', teamId });
    return () => unsubscribe();
  }, [enabled, endISO, startISO, teamId]);

  useEffect(() => {
    if (!enabled || !teamId) return undefined;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return undefined;
    return safeOnSnapshot(
      query(collection(db, 'teams', teamId, 'sailingBoatCharges'), where('memberId', '==', uid)),
      (snapshot) => setCharges(snapshot.docs.map((row) => ({ id: row.id, ...row.data() }))),
      () => setCharges([]),
      { label: 'sailing-boat-booking-member-charges', teamId }
    );
  }, [enabled, teamId]);

  const boatsById = useMemo(() => new Map(boats.map((boat) => [boat.id, boat])), [boats]);
  const activeCount = boats.filter((boat) => String(boat.status || 'active') === 'active').length;
  const chargesById = useMemo(() => new Map(charges.map((charge) => [charge.id, charge])), [charges]);

  async function payCharge(charge) {
    if (!charge?.id || payingChargeId) return;
    setError('');
    setPayingChargeId(charge.id);
    try {
      const checkout = httpsCallable(functions, 'createSailingBoatChargeCheckout');
      const result = await checkout({ teamId, chargeId: charge.id });
      if (!result.data?.url) throw new Error('Payment checkout is unavailable.');
      window.location.assign(result.data.url);
    } catch (err) {
      setError(err?.message || 'Could not open payment checkout.');
      setPayingChargeId('');
    }
  }

  async function cancelRequest(request) {
    if (!teamId || !request?.id) return;
    setError('');
    setCancellingId(request.id);
    try {
      const cancelFn = httpsCallable(functions, 'cancelSailingBoatRequest');
      await cancelFn({ teamId, requestId: request.id, soft: true });
    } catch (err) {
      setError(err?.message || 'Could not cancel this request.');
    } finally {
      setCancellingId('');
    }
  }

  if (!enabled) return null;

  return (
    <div className="team-card boat-booking-card">
      <div className="card-header-with-icon">
        <span className="card-title">Sailing Boat Booking</span>
        {isAdmin && <EditButton section="sailingBoatBookingRules" onEdit={onEditRules} />}
      </div>
      <div className="card-body">
        <div className="availability-status-list">
          <div className="availability-status-item"><span>Active boats</span><span className={activeCount ? 'availability-pill available' : 'availability-pill unavailable'}>{activeCount}</span></div>
          <div className="availability-status-item"><span>Your requests</span><span className="availability-pill info">{requests.filter((row) => String(row.status || '') !== 'cancelled').length}</span></div>
          <div className="availability-status-item"><span>Allocation day</span><span className="availability-pill info">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][Number(rules?.releaseDayOfWeek ?? 1)] || 'Mon'}</span></div>
        </div>

        <div className="form-actions boat-booking-card__primary-actions">
          <button type="button" className="btn btn--primary" onClick={onRequestBoat} disabled={activeCount === 0}>Request a club boat</button>
          <CalendarButton onClick={() => setCalendarOpen(true)}>Boat calendar</CalendarButton>
        </div>

        <div className="boat-booking-card__upcoming-header">
          <h3>Your upcoming</h3>
          <div className="boat-booking-card__date-filters" aria-label="Booking date range">
            <label><span>From</span><input type="date" value={startISO} onChange={(event) => setStartISO(event.target.value)} /></label>
            <label><span>To</span><input type="date" value={endISO} onChange={(event) => setEndISO(event.target.value)} /></label>
          </div>
        </div>

        {error && <p className="text-danger">{error}</p>}
        {loading ? <p className="text-muted">Loading requests…</p> : null}
        {!loading && requests.length === 0 ? <p className="text-muted">No club boat requests in this range.</p> : null}
        <div style={{ display: 'grid', gap: 8, marginTop: 10, maxHeight: 260, overflow: 'auto' }}>
          {requests.filter((row) => String(row.status || '') !== 'cancelled').map((request) => {
            const boatName = request.selectedBoatId ? safeSailingBoatLabel(boatsById.get(request.selectedBoatId) || {}) : '';
            const prefs = Array.isArray(request.preferences) ? request.preferences.map((id) => safeSailingBoatLabel(boatsById.get(id) || {})).filter(Boolean).slice(0, 2) : [];
            const charge = request.chargeId ? chargesById.get(request.chargeId) : null;
            const payable = charge && ['unpaid', 'failed', 'checkout_pending'].includes(String(charge.status || ''));
            return (
              <div key={request.id} className="boat-booking-card__request-item">
                <span className="boat-booking-card__request-details">
                  <strong>{formatDate(request.date)} · {request.fromTime}-{request.toTime}</strong>
                  <small className="text-muted" style={{ display: 'block' }}>{boatName || (prefs.length ? `Prefs: ${prefs.join(', ')}` : 'Awaiting allocation')}</small>
                  {charge && <small className="text-muted" style={{ display: 'block' }}>Payment: {String(charge.status || 'scheduled').replaceAll('_', ' ')} · {formatSailingBoatMoney(charge.checkoutAmountMinor || charge.baseAmountMinor, charge.currency)}</small>}
                </span>
                <span className="boat-booking-card__request-actions">
                  <span className={statusClass(request.status)}>{String(request.status || 'pending')}</span>
                  {payable && <button type="button" className="btn btn--primary btn--xs" onClick={() => payCharge(charge)} disabled={payingChargeId === charge.id}>{payingChargeId === charge.id ? 'Opening…' : 'Pay now'}</button>}
                  <button type="button" className="btn btn--secondary btn--xs" onClick={() => cancelRequest(request)} disabled={cancellingId === request.id}>
                    {cancellingId === request.id ? 'Cancelling…' : 'Cancel'}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {calendarOpen && <BoatCalendarModal isOpen onClose={() => setCalendarOpen(false)} teamId={teamId} boats={boats} selectedBoatId={null} fleet="sailing" />}
    </div>
  );
}
